/**
 * Pur — récupère un access_token Google valide en allant le chercher dans
 * IndexedDB et en refreshant via le Worker proxy si expiré.
 *
 * Aucune dépendance Svelte ni au DOM, pour pouvoir l'importer depuis le
 * service worker (`service-worker.ts`) en plus de la page (`oauth.ts`).
 *
 * Renvoie `null` si pas de tokens stockés OU si refresh fail. Ne CLEAR pas
 * les tokens en cas d'échec : c'est volontaire, le SW ne doit pas pouvoir
 * « sign-out » l'utilisateur silencieusement (un échec réseau transitoire
 * en arrière-plan ne doit pas casser l'auth de la page). Le wrapper
 * `oauth.ts::getAccessToken` côté page applique la politique « clear si
 * refresh fail » qui suppose que la page peut prompt un re-sign-in.
 */

import { loadTokens, saveTokens, type AuthTokens } from "./storage";
import { oauthRefresh } from "../workerClient";

export async function getValidAccessToken(): Promise<string | null> {
  const tokens = await loadTokens();
  if (!tokens) return null;

  // Encore valide (safety margin déjà appliquée à expires_at à la sauvegarde)
  if (Date.now() < tokens.expires_at) {
    return tokens.access_token;
  }

  try {
    const refreshed = await oauthRefresh({ refresh_token: tokens.refresh_token });
    if (!refreshed.access_token) return null;

    const updated: AuthTokens = {
      ...tokens,
      access_token: refreshed.access_token,
      expires_at: Date.now() + (refreshed.expires_in - 60) * 1000,
      // Google peut occasionnellement rotater le refresh_token
      refresh_token: refreshed.refresh_token ?? tokens.refresh_token,
    };
    await saveTokens(updated);
    return updated.access_token;
  } catch (e) {
    console.warn("getValidAccessToken: refresh failed:", e);
    return null;
  }
}
